// Radha — the withholding-tax (TDS/WHT) desk (074).
//
// Three sections, in the order a finance team works them:
//   1. Candidates — short-paid invoices whose gap matches a statutory rate.
//      One click recognises the gap as WHT, which removes it from arrears.
//   2. Certificates receivable — the recognised credits and their lifecycle
//      (expected -> certificate received -> matched in 26AS / written off).
//   3. The rulebook for the tenant's country, with statutes cited.
import React, { useState } from "react";

import {
  useWhtCandidates, useWhtCredits, useWithholdingRules,
  useRecogniseWht, useAdvanceWht, deskErrorMessage,
  type WhtCredit,
} from "../hooks/useGlobalDesk";
import { useMe } from "../lib/authSupabase";
import { useViewCountry, countryDef } from "../lib/jurisdiction";
import { useRole } from "../lib/authSupabase";
import { fmtMoney, fmtMoneyFull } from "../lib/money";
import { ExportButton } from "../components/TableSearch";

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "8px" };
const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

const CREDIT_FLOW: Record<WhtCredit["status"], { label: string; next?: WhtCredit["status"]; nextLabel?: string; color: string }> = {
  expected:             { label: "Expected",            next: "certificate_received", nextLabel: "certificate received", color: "var(--status-warning)" },
  certificate_received: { label: "Certificate received", next: "matched_26as",        nextLabel: "matched in 26AS",      color: "var(--accent-purple)" },
  matched_26as:         { label: "Matched (26AS)",       color: "var(--status-good)" },
  mismatch:             { label: "Mismatch",             next: "certificate_received", nextLabel: "re-received",          color: "var(--status-critical)" },
  written_off:          { label: "Written off",          color: "var(--fg-faint)" },
};

export function WithholdingPanel() {
  const me = useMe(true);
  const viewCountry = useViewCountry(me?.tenant_country);
  const { canWrite } = useRole();
  const { data: candidates, isLoading, error } = useWhtCandidates(viewCountry);
  const { data: credits } = useWhtCredits();
  const { data: rules } = useWithholdingRules(viewCountry);
  const recognise = useRecogniseWht();
  const advance = useAdvanceWht();
  const [note, setNote] = useState<string | null>(null);

  if (isLoading) return <p style={{ padding: 24, color: "var(--fg-muted)" }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  const cands = candidates ?? [];
  const creds = credits ?? [];
  const candTotal = cands.reduce((s, c) => s + Number(c.gap), 0);
  const open = creds.filter((c) => c.status === "expected" || c.status === "certificate_received" || c.status === "mismatch");
  const openAmt = open.reduce((s, c) => s + Number(c.amount), 0);
  const matched = creds.filter((c) => c.status === "matched_26as").reduce((s, c) => s + Number(c.amount), 0);

  return (
    <div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "4px 0 16px", maxWidth: 760 }}>
        When a customer legally withholds tax from a payment (TDS here; 1042/WHT abroad), the shortfall is not
        arrears — it is a credit recoverable from the tax department, provided the certificate arrives and matches.
        This desk finds those short-pays, takes them out of collections, and chases the certificate instead.
      </p>

      <div style={{ display: "flex", gap: 24, margin: "4px 0 20px", flexWrap: "wrap" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Unrecognised candidates</div>
          <div style={{ font: "600 20px Inter" }}>{cands.length}
            <span style={{ fontSize: 13, fontWeight: 400, color: "var(--fg-muted)" }}> · {fmtMoney(candTotal)}</span></div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Certificates receivable</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-warning)" }}>{fmtMoney(openAmt)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Matched in 26AS</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-good)" }}>{fmtMoney(matched)}</div></div>
      </div>
      {note && <p style={{ fontSize: 12.5, color: "var(--status-critical)", margin: "0 0 10px" }}>{note}</p>}

      <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ font: "600 15px Inter", marginBottom: 8 }}>Detected candidates</div>
          <ExportButton name="WHT candidates" rows={() => cands.map((canRow) => ({
            Invoice: canRow.invoice_number, Customer: canRow.customer_name, Gap: Number(canRow.gap),
            Section: canRow.matched_section, "Rate %": Number(canRow.matched_rate), Base: canRow.matched_base,
          }))} />
        </div>
        <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px" }}>
          Invoices where the unpaid remainder matches a statutory rate within ±0.35pp — tested against both the
          taxable value and the gross, since customers compute it wrongly in both directions.
        </p>
        <div className="sanctuary-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
              <th style={th}>Invoice</th><th style={th}>Customer</th>
              <th style={{ ...th, textAlign: "right" }}>Invoice value</th>
              <th style={{ ...th, textAlign: "right" }}>Short-paid</th>
              <th style={th}>Matches</th><th style={th}>Off by</th><th style={th} />
            </tr></thead>
            <tbody>
              {cands.map((c) => (
                <tr key={c.invoice_id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ ...td, fontSize: 12 }}>{c.invoice_number}</td>
                  <td style={td}>{c.customer_name}</td>
                  <td style={num}>{fmtMoney(c.total_amount)}</td>
                  <td style={{ ...num, fontWeight: 600 }}>{fmtMoneyFull(c.gap)}</td>
                  <td style={td}>
                    <span style={{ color: "var(--accent-purple)" }}>§{c.matched_section}</span>
                    <span style={{ color: "var(--fg-muted)" }}> @ {Number(c.matched_rate)}% of {c.matched_base}</span>
                  </td>
                  <td style={{ ...td, color: "var(--fg-faint)", fontSize: 12 }}>{Number(c.deviation_pp).toFixed(2)}pp</td>
                  <td style={td}>
                    {canWrite && (
                      <button onClick={() => { setNote(null); recognise.mutate(c, { onError: (e) => setNote(deskErrorMessage(e)) }); }}
                        disabled={recognise.isPending}
                        style={{ padding: "3px 10px", fontSize: 11.5, border: 0, borderRadius: 6, background: "var(--accent-purple)", color: "#fff", cursor: "pointer" }}>
                        recognise as WHT
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {cands.length === 0 && <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={7}>No unexplained short-pays match a withholding rate.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ font: "600 15px Inter", marginBottom: 8 }}>Certificates receivable</div>
        <div className="sanctuary-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
              <th style={th}>Customer</th><th style={{ ...th, textAlign: "right" }}>Amount</th>
              <th style={th}>Section</th><th style={th}>Status</th><th style={th}>Certificate</th><th style={th} />
            </tr></thead>
            <tbody>
              {creds.map((c) => {
                const flow = CREDIT_FLOW[c.status];
                return (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={td}>{c.customer_name}</td>
                    <td style={num}>{fmtMoneyFull(c.amount, c.currency)}</td>
                    <td style={{ ...td, color: "var(--fg-muted)" }}>{c.rule_section ? `§${c.rule_section}` : "—"}</td>
                    <td style={{ ...td, color: flow.color }}>{flow.label}{c.resolved_date && <span style={{ color: "var(--fg-faint)" }}> · {c.resolved_date}</span>}</td>
                    <td style={{ ...td, color: "var(--fg-faint)", fontSize: 12 }}>{c.certificate_ref ?? "—"}</td>
                    <td style={td}>
                      {canWrite && flow.next && (
                        <button onClick={() => advance.mutate({ id: c.id, status: flow.next!, certificate_ref: c.certificate_ref ?? undefined })}
                          disabled={advance.isPending}
                          style={{ padding: "2px 8px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
                          mark {flow.nextLabel}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {creds.length === 0 && <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={6}>Nothing recognised yet — start from the candidates above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sanctuary-card" style={{ padding: "14px 16px" }}>
        <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Rulebook — {countryDef(viewCountry)?.label ?? viewCountry}</div>
        <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px" }}>
          Rows marked statutory carry the citation; the rest are representative defaults that treaty positions can
          change — confirm before relying on them.
        </p>
        <div className="sanctuary-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
              <th style={th}>Section</th><th style={th}>What</th>
              <th style={{ ...th, textAlign: "right" }}>Rate</th><th style={th}>Base</th><th style={th}>Statute</th>
            </tr></thead>
            <tbody>
              {(rules ?? []).map((r) => (
                <tr key={r.section} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 11.5 }}>{r.section}</td>
                  <td style={td}>{r.label}
                    {!r.is_statutory && <span style={{ marginInlineStart: 6, fontSize: 10, color: "var(--status-warning)", border: "1px solid currentColor", borderRadius: 4, padding: "0 4px" }}>confirm</span>}
                  </td>
                  <td style={num}>{Number(r.rate_pct)}%</td>
                  <td style={{ ...td, color: "var(--fg-muted)" }}>{r.base}</td>
                  <td style={{ ...td, color: "var(--fg-faint)", fontSize: 11.5 }}>{r.statute_ref ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
